import { create } from 'zustand'

type UnreadCountStore = {
  counts: Record<string, number>
  updateCounts: (partial: Record<string, number>) => void
  getCount: (slug?: string) => number
  /**
   * Resolve badge count for a container app. Prefers `appId` (notification
   * `containerAppSlug`) over `notificationSlug` (`applicationSlug` / config.slug).
   *
   * Uses `get().counts` (live store) rather than a selector snapshot — intentional:
   * the return value is a primitive so Zustand equality still works; matches `getCount`.
   */
  getCountForApp: (appId?: string, notificationSlug?: string) => number
}

function readCountForKey(
  counts: Record<string, number>,
  key: string | undefined
): number | undefined {
  if (typeof key !== 'string') return undefined
  const trimmed = key.trim()
  if (!trimmed) return undefined
  if (!Object.hasOwn(counts, trimmed)) return undefined
  return counts[trimmed]
}

export const useUnreadCountStore = create<UnreadCountStore>((set, get) => ({
  counts: {},

  updateCounts: (partial: Record<string, number>) =>
    set(state => ({ counts: { ...state.counts, ...partial } })),

  getCount: (slug?: string) => {
    if (!slug) return 0
    return get().counts[slug] ?? 0
  },

  getCountForApp: (appId?: string, notificationSlug?: string) => {
    const counts = get().counts
    const fromAppId = readCountForKey(counts, appId)
    if (fromAppId !== undefined) return fromAppId
    const fromNotificationSlug = readCountForKey(counts, notificationSlug)
    if (fromNotificationSlug !== undefined) return fromNotificationSlug
    return 0
  }
}))

/** Aggregate unread for the notification bell (payload key from notification app / legacy). */
export const selectNotificationBellUnreadCount = (state: {
  getCount: (slug?: string) => number
}): number => state.getCount('notifications')
